import type {
  PromiseToastOptions,
  ToastApi,
  ToastId,
  ToastInstance,
  ToastOptions,
} from './toast-types';

const DEFAULT_DURATION = 5000;

function omitUndefined(options: ToastOptions): ToastOptions {
  return Object.fromEntries(
    Object.entries(options).filter(([, value]) => value !== undefined)
  ) as ToastOptions;
}

export interface ToastStore {
  api: ToastApi;
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => ToastInstance[];
  /** Suspend a toast's auto-dismiss timer (e.g. while hovered). */
  pauseTimer: (id: ToastId) => void;
  /** Restart a paused toast's auto-dismiss timer at its full duration. */
  resumeTimer: (id: ToastId) => void;
}

export function createToastStore(): ToastStore {
  let toasts: ToastInstance[] = [];
  let counter = 0;
  const listeners = new Set<() => void>();
  const timers = new Map<ToastId, ReturnType<typeof setTimeout>>();
  const paused = new Set<ToastId>();

  function emit() {
    for (const listener of listeners) {
      listener();
    }
  }

  function clearTimer(id: ToastId) {
    const timer = timers.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      timers.delete(id);
    }
  }

  function scheduleClose(instance: ToastInstance) {
    clearTimer(instance.id);
    if (instance.duration !== null && !paused.has(instance.id)) {
      timers.set(
        instance.id,
        setTimeout(() => close(instance.id), instance.duration)
      );
    }
  }

  function normalize(options: ToastOptions): ToastInstance {
    counter += 1;
    return {
      duration: DEFAULT_DURATION,
      status: 'info',
      position: 'top-right',
      isClosable: true,
      ...omitUndefined(options),
      id: options.id ?? `toast-${counter}`,
    };
  }

  function add(options: ToastOptions): ToastId {
    const instance = normalize(options);
    const existingIndex = toasts.findIndex((t) => t.id === instance.id);
    toasts =
      existingIndex >= 0
        ? toasts.map((t) => (t.id === instance.id ? instance : t))
        : [...toasts, instance];
    scheduleClose(instance);
    emit();
    return instance.id;
  }

  function close(id: ToastId) {
    const instance = toasts.find((t) => t.id === id);
    if (instance === undefined) {
      return;
    }
    clearTimer(id);
    paused.delete(id);
    toasts = toasts.filter((t) => t.id !== id);
    emit();
    instance.onCloseComplete?.();
  }

  function closeAll() {
    for (const instance of [...toasts]) {
      close(instance.id);
    }
  }

  function isActive(id: ToastId): boolean {
    return toasts.some((t) => t.id === id);
  }

  function update(id: ToastId, options: ToastOptions) {
    const existing = toasts.find((t) => t.id === id);
    if (existing === undefined) {
      return;
    }
    const merged: ToastInstance = {
      ...existing,
      // Content is rebuilt from the new options (Chakra parity): an update
      // without `render` reverts the toast to the standard title layout.
      title: options.title,
      description: options.description,
      render: options.render,
      ...omitUndefined(options),
      id,
    };
    toasts = toasts.map((t) => (t.id === id ? merged : t));
    scheduleClose(merged);
    emit();
  }

  function promiseToast<T>(
    promise: Promise<T>,
    options: PromiseToastOptions<T>
  ): Promise<T> {
    // Non-dismissable by default (Chakra parity): closing the loading toast
    // would orphan the settled update below, silently dropping the outcome.
    const id = add({
      isClosable: false,
      ...options.loading,
      status: 'loading',
      duration: null,
    });
    promise.then(
      (data) => {
        const next =
          typeof options.success === 'function'
            ? options.success(data)
            : options.success;
        update(id, {
          duration: DEFAULT_DURATION,
          isClosable: true,
          ...next,
          status: 'success',
        });
      },
      (error) => {
        const next =
          typeof options.error === 'function'
            ? options.error(error)
            : options.error;
        update(id, {
          duration: DEFAULT_DURATION,
          isClosable: true,
          ...next,
          status: 'error',
        });
      }
    );
    return promise;
  }

  const api = ((options: ToastOptions) => add(options)) as ToastApi;
  api.isActive = isActive;
  api.close = close;
  api.closeAll = closeAll;
  api.update = update;
  api.promise = promiseToast;

  return {
    api,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot: () => toasts,
    pauseTimer: (id) => {
      paused.add(id);
      clearTimer(id);
    },
    resumeTimer: (id) => {
      paused.delete(id);
      const instance = toasts.find((t) => t.id === id);
      if (instance !== undefined) {
        scheduleClose(instance);
      }
    },
  };
}
